import {
  CONTRACTOR_BADGE_GRADING_DECISION,
  type IncludedFinalGrade,
  type IncludedShift,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";

import {
  BadgeVerificationWindowDays,
  useGetWorkerBadgeVerifications,
} from "../api/useGetWorkerBadgeVerifications";

export enum BadgeVerificationResultType {
  SHOW_SKIPPED = "SHOW_SKIPPED",
  SHOW_MISSED = "SHOW_MISSED",
}

interface UseShouldShowBadgeVerificationResultParams {
  workerId: string;
  enabled?: boolean;
}

type VerificationsResponse = ReturnType<typeof useGetWorkerBadgeVerifications>["data"];
type Verification = NonNullable<VerificationsResponse>["data"][0];
type Included = NonNullable<VerificationsResponse>["included"];

interface ClassifiedVerification {
  verification: Verification;
  resultType: BadgeVerificationResultType;
  finalGrade?: IncludedFinalGrade;
}

interface BadgeVerificationResultData {
  verification: Verification;
  finalGrade?: IncludedFinalGrade;
  shiftId: string;
  qualificationName: string | undefined;
}

interface UseShouldShowBadgeVerificationResultReturn {
  type: BadgeVerificationResultType | undefined;
  isLoading: boolean;
  data: BadgeVerificationResultData | undefined;
  strikeCount: number;
}

/**
 * Classifies a single verification as skipped or missed (incorrect grade).
 * Returns undefined if the verification doesn't qualify for display
 * (already seen, still being reviewed, or passed).
 */
function classifyVerification(
  verification: Verification,
  included: NonNullable<Included>
): ClassifiedVerification | undefined {
  if (isDefined(verification.attributes.resultSeenByWorkerAt)) {
    return undefined;
  }

  if (isDefined(verification.attributes.skippedAt)) {
    return { verification, resultType: BadgeVerificationResultType.SHOW_SKIPPED };
  }

  const gradeId = verification.relationships.finalGrade?.data?.id;
  if (!isDefined(gradeId)) {
    return undefined;
  }

  const finalGrade = included.find(
    (resource): resource is IncludedFinalGrade =>
      resource.type === "contractor-badge-verification-grade" && resource.id === gradeId
  );

  if (
    !isDefined(finalGrade) ||
    finalGrade.attributes.decision !== CONTRACTOR_BADGE_GRADING_DECISION.INCORRECT_BADGE_SUBMISSION
  ) {
    return undefined;
  }

  return { verification, resultType: BadgeVerificationResultType.SHOW_MISSED, finalGrade };
}

export function useShouldShowBadgeVerificationResult(
  params: UseShouldShowBadgeVerificationResultParams
): UseShouldShowBadgeVerificationResultReturn {
  const { workerId, enabled = true } = params;

  const {
    data: response,
    isLoading,
    missedVerifications,
  } = useGetWorkerBadgeVerifications(workerId, BadgeVerificationWindowDays.MISSED_BADGES, {
    includeMissedBadges: true,
    enabled,
  });

  const included = response?.included ?? [];
  const strikeCount = missedVerifications.length;

  // Find the first unseen verification that is either skipped or graded as incorrect
  let classified: ClassifiedVerification | undefined;
  for (const verification of response?.data ?? []) {
    const result = classifyVerification(verification, included);
    if (isDefined(result)) {
      classified = result;
      break;
    }
  }

  if (!isDefined(classified)) {
    return { type: undefined, isLoading, data: undefined, strikeCount };
  }

  const shiftId = classified.verification.relationships.shift.data.id;
  const includedShift = included.find(
    (resource): resource is IncludedShift => resource.type === "shift" && resource.id === shiftId
  );
  const isCurrentlyClockedIn =
    isDefined(includedShift?.attributes.clockedInAt) &&
    !isDefined(includedShift?.attributes.clockedOutAt);

  if (isCurrentlyClockedIn) {
    return { type: undefined, isLoading, data: undefined, strikeCount };
  }

  return {
    type: classified.resultType,
    isLoading,
    strikeCount,
    data: {
      verification: classified.verification,
      finalGrade: classified.finalGrade,
      shiftId,
      qualificationName: includedShift?.attributes.qualification,
    },
  };
}
